// RGBA 配列 → 画像 Blob。PNG はロスレス (fast-png)、JPEG/WebP は Canvas。

import { encode as encodePng } from 'fast-png'

export type ExportFormat = 'png' | 'jpeg' | 'webp' | 'avif'

export interface EncodeOptions {
  format: ExportFormat
  /** 0..1 (jpeg / webp) */
  quality?: number
}

export const FORMAT_MIME: Record<ExportFormat, string> = { png: 'image/png', jpeg: 'image/jpeg', webp: 'image/webp', avif: 'image/avif' }
export const FORMAT_EXT: Record<ExportFormat, string> = { png: 'png', jpeg: 'jpg', webp: 'webp', avif: 'avif' }

/** ブラウザがそのエンコードに対応しているか (実際に 1px を変換して確認) */
const supportCache = new Map<ExportFormat, Promise<boolean>>()
export function canEncode(format: ExportFormat): Promise<boolean> {
  if (format === 'png') return Promise.resolve(true)
  let p = supportCache.get(format)
  if (!p) {
    p = (async () => {
      try {
        const c = new OffscreenCanvas(1, 1)
        c.getContext('2d')!.fillRect(0, 0, 1, 1)
        const b = await c.convertToBlob({ type: FORMAT_MIME[format] })
        return b.type === FORMAT_MIME[format]
      } catch {
        return false
      }
    })()
    supportCache.set(format, p)
  }
  return p
}

export async function encodeRgba(width: number, height: number, rgba: Uint8ClampedArray, opts: EncodeOptions): Promise<Blob> {
  if (opts.format === 'png') {
    const bytes = encodePng({ width, height, data: new Uint8Array(rgba.buffer, rgba.byteOffset, rgba.byteLength), channels: 4, depth: 8 })
    return new Blob([bytes as BlobPart], { type: 'image/png' })
  }
  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2D コンテキストが取得できない')
  if (opts.format === 'jpeg') {
    // JPEG は透明を持てないので白の上に合成する
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    const tmp = new OffscreenCanvas(width, height)
    const tctx = tmp.getContext('2d')!
    tctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), width, height), 0, 0)
    ctx.drawImage(tmp, 0, 0)
  } else {
    ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), width, height), 0, 0)
  }
  return canvas.convertToBlob({ type: FORMAT_MIME[opts.format], quality: opts.quality ?? 0.92 })
}

/** 単純なボックス縮小 (書き出しの倍率用)。拡大は最近傍 */
export function resampleRgba(width: number, height: number, rgba: Uint8ClampedArray, scale: number): { width: number; height: number; rgba: Uint8ClampedArray } {
  const w2 = Math.max(1, Math.round(width * scale))
  const h2 = Math.max(1, Math.round(height * scale))
  if (w2 === width && h2 === height) return { width, height, rgba }
  const out = new Uint8ClampedArray(w2 * h2 * 4)
  for (let y = 0; y < h2; y++) {
    const sy0 = Math.floor((y * height) / h2)
    const sy1 = Math.max(sy0 + 1, Math.floor(((y + 1) * height) / h2))
    for (let x = 0; x < w2; x++) {
      const sx0 = Math.floor((x * width) / w2)
      const sx1 = Math.max(sx0 + 1, Math.floor(((x + 1) * width) / w2))
      let r = 0, g = 0, b = 0, a = 0, n = 0
      for (let sy = sy0; sy < sy1; sy++) {
        for (let sx = sx0; sx < sx1; sx++) {
          const i = (sy * width + sx) * 4
          const al = rgba[i + 3]!
          r += rgba[i]! * al; g += rgba[i + 1]! * al; b += rgba[i + 2]! * al; a += al; n++
        }
      }
      const o = (y * w2 + x) * 4
      if (a > 0) { out[o] = r / a; out[o + 1] = g / a; out[o + 2] = b / a }
      out[o + 3] = a / n
    }
  }
  return { width: w2, height: h2, rgba: out }
}
